/**
 * Fetching generated documents.
 *
 * Documents come back as files, not JSON, so they cannot go through `api.get`.
 * They are fetched rather than linked to because a bare `<a href>` cannot report
 * a failure: a 404 or an expired session would render the error JSON in a new
 * tab as though it were the receipt.
 */

import { ApiError, noticeSessionEnded } from './api';

const BASE_URL = import.meta.env['VITE_API_BASE_URL'] || '/api';

/**
 * No `csv`. It existed only for the four range reports, which were removed
 * along with the server-side renderer they were the only caller of.
 */
export type DocumentFormat = 'text' | 'pdf';

/**
 * Fetches a document and hands back an object URL plus the filename the server
 * chose.
 *
 * The caller must call `revoke()` when finished. An object URL pins its blob in
 * memory for the lifetime of the document, and a few dozen unreleased PDFs is
 * real memory on a phone.
 */
export async function fetchDocument(
  path: string,
  format: DocumentFormat,
): Promise<{ url: string; filename: string; contentType: string; revoke: () => void }> {
  const separator = path.includes('?') ? '&' : '?';
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}${separator}format=${format}`, {
      credentials: 'include',
    });
  } catch {
    throw new ApiError(0, 'Cannot reach QuickStock. Check your connection.');
  }

  noticeSessionEnded(response);
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      requestId?: string;
    } | null;
    throw new ApiError(
      response.status,
      body?.error ?? `Could not generate the document (${response.status})`,
      body?.requestId,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  return {
    url,
    filename: filenameFrom(response.headers.get('content-disposition')) ?? 'document',
    // What the server actually sent, which is not always what was asked for.
    // Receipts exist in the store's bucket only as the till wrote them — text —
    // so a request for PDF comes back as text/plain. Rendering by the requested
    // format rather than the served one would embed those bytes in a PDF frame.
    contentType: response.headers.get('content-type') ?? 'application/octet-stream',
    revoke: () => URL.revokeObjectURL(url),
  };
}

function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? null;
}
